import { computed, onMounted, onBeforeUnmount, useHost, type ComputedRef } from "vue";
import type { SelectItemProps } from "./select.types";
import type { ListboxItemProps } from "../Listbox/listbox.types";
import { useSharedState } from "composables/useSharedState";
import { useSelect, registerSelectItem, unregisterSelectItem } from "./useSelect";
import { useListbox } from "../Listbox/useListbox";

export interface UseSelectItemOptions {
  props: Readonly<SelectItemProps>;
}

export interface UseSelectItemReturn {
  /** Override for ListboxItem highlighted state */
  isHighlighted: ComputedRef<boolean>;
  /** Whether item is selected */
  isSelected: ComputedRef<boolean>;
  /** Whether item is disabled */
  isDisabled: ComputedRef<boolean>;
  /** Style for typeahead ordering */
  itemStyle: ComputedRef<{ order: number }>;
  /** Style props from parent select */
  color: ComputedRef<ListboxItemProps["color"]>;
  variant: ComputedRef<ListboxItemProps["variant"]>;
  size: ComputedRef<ListboxItemProps["size"]>;
  /** Handle select event from ListboxItem */
  handleSelect: () => void;
}

export const useSelectItem = (options: UseSelectItemOptions): UseSelectItemReturn => {
  const { props } = options;
  const host = useHost();

  // Register in state store for keyboard navigation discovery
  useSharedState(props);

  const itemValue = computed(() => props.value);

  // Connect to parent Select and Listbox
  const select = useSelect({ itemValue });
  const listbox = useListbox({ itemValue });

  // Computed state for ListboxItem overrides
  const isHighlighted = computed(() => {
    const isTypeaheadMatch = listbox.isTypeaheadMatch.value;
    return select.isHighlighted.value || isTypeaheadMatch;
  });
  const isSelected = computed(() => select.isSelected.value);
  const isDisabled = computed(() => props.disabled || select.isDisabled.value);

  // Style props - item props take precedence over parent
  const color = computed(() => props.color ?? select.selectProps.value?.color);
  const variant = computed(() => props.variant ?? select.selectProps.value?.variant);
  const size = computed(() => props.size ?? select.selectProps.value?.size);

  // Compute order style for typeahead filtering
  const itemStyle = computed(() => ({
    order: listbox.getItemTypeaheadOrder(props.value),
  }));

  // Cache select element for registration/unregistration
  let cachedSelectEl: Element | null = null;

  // Register item label for Select (used for display value in trigger)
  onMounted(() => {
    cachedSelectEl = host?.closest("e-select") ?? select.selectElement.value;

    if (props.value !== undefined && cachedSelectEl) {
      const label = props.label ?? host?.textContent?.trim() ?? String(props.value);
      registerSelectItem(cachedSelectEl, props.value, label);
    }
  });

  onBeforeUnmount(() => {
    if (props.value !== undefined && cachedSelectEl) {
      unregisterSelectItem(cachedSelectEl, props.value);
    }
  });

  // Handle select event from ListboxItem
  const handleSelect = () => {
    if (isDisabled.value) return;

    // Toggle selection in parent Select
    select.toggleSelection(itemValue.value);
  };

  return {
    isHighlighted,
    isSelected,
    isDisabled,
    itemStyle,
    color,
    variant,
    size,
    handleSelect,
  };
};
